import { blobToHex } from '@pkg/basic'
import { and, eq, isNull } from 'drizzle-orm'
import { accountTable, actionAccountRelationTable } from '../../schema'
import type { AccountEvm, DatabaseWithTransaction } from '../../types'

/**
 * Parameters for batch retrieving EVM accounts without action data
 */
type SelectEvmAccountsWithoutActionsParameters = {
  /** Database connection with transaction support */
  database: DatabaseWithTransaction
}

/**
 * Batch retrieves accounts with type = 'evm' that do not have corresponding action data.
 *
 * This function performs a LEFT JOIN between the account table and the action-account
 * relation table to find EVM accounts that have no associated actions. This is useful
 * for identifying accounts that need to be analyzed for the first time or have been
 * recently added but not yet processed.
 *
 * @param params - Configuration parameters including database connection and pagination options
 * @returns Promise resolving to an array of EVM accounts without actions
 *
 * @example
 * ```typescript
 * const accounts = await selectEvmAccountsWithoutActions({
 *   database: dbConnection,
 * });
 * ```
 */
export const selectEvmAccountsWithoutActions = async ({
  database: { database },
}: SelectEvmAccountsWithoutActionsParameters): Promise<
  readonly AccountEvm[]
> => {
  try {
    const rows = await database
      .select({
        id: accountTable.id,
        name: accountTable.name,
        group: accountTable.group,
        type: accountTable.type,
        category: accountTable.category,
        evmChainId: accountTable.evmChainId,
        evmAddress: accountTable.evmAddress,
        evmToBlock: accountTable.evmToBlock,
        evmLastTxCount: accountTable.evmLastTxCount,
        syncing: accountTable.syncing,
        lastSyncedAt: accountTable.lastSyncedAt,
      })
      .from(accountTable)
      .leftJoin(
        actionAccountRelationTable,
        eq(accountTable.id, actionAccountRelationTable.accountId),
      )
      .where(
        and(
          eq(accountTable.type, 'evm'),
          isNull(actionAccountRelationTable.accountId),
        ),
      )

    // Transform database rows to AccountEvm objects
    const accounts: AccountEvm[] = []
    for (const row of rows) {
      if (row.type !== 'evm' || !row.evmChainId || !row.evmAddress) {
        continue
      }
      accounts.push({
        id: row.id,
        name: row.name,
        group: row.group,
        type: 'evm',
        category: row.category,
        evmChainId: row.evmChainId,
        evmAddress: blobToHex(row.evmAddress),
        syncing: row.syncing ?? false,
        lastSyncedAt: row.lastSyncedAt?.getTime(),
        lastTransactionCount: row.evmLastTxCount ?? undefined,
        evmToBlock: row.evmToBlock ?? undefined,
      })
    }
    return accounts
  } catch (error) {
    throw new Error(
      `Failed to select EVM accounts without actions: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}
